import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
const MODULE_KEY = "affiliate_account_performance";
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const initialState = {
    lists: [],
    lastUpdatedAt: null,
    trackerUpdatedAt: null,
    currentListDetails: [],
    loading: false,
    error: null,
    searchName: "",
    selectedClientId: "",
    selectedStatus: "",
    selectedTrackerType: "",
    sortBy: "createdAt",
    sortOrder: "desc",
};
// ==========================================================================
// ASYNC THUNKS
// ==========================================================================
const getApiData = (response) => response?.data ?? response?.response?.data;
const getApiErrorMessage = (response, err, fallback) => {
    const data = getApiData(response);
    return data?.message || err?.response?.data?.message || err?.message || fallback;
};
// 1. Fetch tracker lists
export const fetchSocialYtTrackerLists = createAsyncThunk("socialYtTracker/fetchLists", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState().socialYtTracker;
        const token = getState().auth?.token || "";
        let url = "/api/social-yt-tracker/list?";
        if (state.searchName)
            url += `name=${encodeURIComponent(state.searchName)}&`;
        if (state.selectedClientId)
            url += `client_id=${encodeURIComponent(state.selectedClientId)}&`;
        if (state.selectedStatus)
            url += `status=${encodeURIComponent(state.selectedStatus)}&`;
        if (state.selectedTrackerType)
            url += `tracker_type=${encodeURIComponent(state.selectedTrackerType)}&`;
        if (state.sortBy)
            url += `sortBy=${encodeURIComponent(state.sortBy)}&`;
        if (state.sortOrder)
            url += `sortOrder=${encodeURIComponent(state.sortOrder)}&`;
        const response = await ApiCall("GET", url, undefined, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = getApiData(response);
        if (data?.success)
            return { lists: data.data?.lists || [], lastUpdatedAt: data.data?.lastUpdatedAt || null };
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch tracker lists"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 2. Fetch tracker details (contacts)
export const fetchSocialYtTrackerDetails = createAsyncThunk("socialYtTracker/fetchDetails", async ({ id, noContact }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const url = `/api/social-yt-tracker/details?id=${id}${noContact ? "&no_contact=true" : ""}`;
        const response = await ApiCall("GET", url, undefined, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = getApiData(response);
        if (data?.success)
            return { contacts: data.data?.contacts || [], trackerUpdatedAt: data.data?.trackerUpdatedAt || null };
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to fetch tracker details"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 3. Manage tracker (update_contact / delete_contact / update_tracker / delete_tracker)
export const manageSocialYtTracker = createAsyncThunk("socialYtTracker/manage", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/social-yt-tracker/manage", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to manage tracker"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 4. Import Excel
export const importSocialYtExcel = createAsyncThunk("socialYtTracker/importExcel", async (formData, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/social-yt-tracker/import-excel", formData, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
            "Content-Type": "multipart/form-data"
        });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to import Excel"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 5. Send for Approval
export const sendSocialYtForApproval = createAsyncThunk("socialYtTracker/sendForApproval", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/social-yt-tracker/send-for-approval", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to send for approval"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// 6. Client respond (Approve / Reject social/yt contact)
export const clientRespondSocialYt = createAsyncThunk("socialYtTracker/clientRespond", async (payload, { rejectWithValue }) => {
    try {
        const response = await ApiCall("POST", "/api/social-yt-tracker/client-respond", payload, undefined);
        const data = getApiData(response);
        if (data?.success)
            return data.data;
        return rejectWithValue(getApiErrorMessage(response, null, "Failed to submit response"));
    }
    catch (err) {
        return rejectWithValue(getApiErrorMessage(null, err, "An error occurred"));
    }
});
// ==========================================================================
// SLICE
// ==========================================================================
const socialYtTrackerSlice = createSlice({
    name: "socialYtTracker",
    initialState,
    reducers: {
        setSocialYtFilters: (state, action) => {
            return { ...state, ...action.payload };
        },
        clearSocialYtCurrentDetails: (state) => {
            state.currentListDetails = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSocialYtTrackerLists.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchSocialYtTrackerLists.fulfilled, (state, action) => { state.loading = false; state.lists = action.payload.lists; state.lastUpdatedAt = action.payload.lastUpdatedAt; })
            .addCase(fetchSocialYtTrackerLists.rejected, (state, action) => { state.loading = false; state.error = action.payload || "Failed to fetch lists"; })
            .addCase(fetchSocialYtTrackerDetails.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchSocialYtTrackerDetails.fulfilled, (state, action) => { state.loading = false; state.currentListDetails = action.payload.contacts; state.trackerUpdatedAt = action.payload.trackerUpdatedAt; })
            .addCase(fetchSocialYtTrackerDetails.rejected, (state, action) => { state.loading = false; state.error = action.payload || "Failed to fetch details"; });
    }
});
export const { setSocialYtFilters, clearSocialYtCurrentDetails } = socialYtTrackerSlice.actions;
export default socialYtTrackerSlice.reducer;
